// npm
import React from 'react'
import {connect} from 'react-redux'
import check from 'check-types'

// material-ui
import Paper from '@material-ui/core/Paper'
import withStyles from '@material-ui/core/styles/withStyles'

// react
import {WorkingsTabs} from '../../side/panels/workings/workings-tabs'
import {Calculator} from '../../side/panels/workings/calculator'

// redux (selectors)
import {getExamData, getSettingsData} from 'redux/reducers/selectors'
import {getContentData} from 'redux/reducers/exam/selectors'
import {getClientSettingsData} from 'redux/reducers/settings/selectors'
import {getCalculatorEvaluationMode, getWorkingsCalcMode} from 'redux/reducers/settings/client/selectors'
import {getQuestionDataById} from 'redux/reducers/exam/content/questions/selectors'
import {getWorkingsData} from 'redux/reducers/exam/content/questions/question/selectors'
import {getCurrentQuestionId, getQuestionsData} from 'redux/reducers/exam/content/selectors'
import {getWorkings} from 'redux/reducers/exam/content/questions/question/workings/selectors'
import {requiresWorkings, requiresCalculator} from 'redux/reducers/exam/content/complex-selectors'

// redux (actions)
import {saveWorkings} from '../../side/panels/workings/actions'

// utils
import {addCalculatorSymbols} from '../../side/panels/workings/calculator-helper'


const setFocusPosition = (elem, caretPos) => {
  if (elem != null) {
    if (elem.createTextRange) {
      var range = elem.createTextRange();
      range.move('character', caretPos);
      range.select();
    } else {
      try {
        elem.focus();
        elem.setSelectionRange(caretPos, caretPos);
      } catch (e) {
        elem.focus();
      }
    }
  }
};


// InlineWorkingsPanel (not connected to store or styles)
// ------------------------------------------------------

class InlineWorkingsPanel extends React.Component {
  constructor(props) {
    super(props);
    this.workingsRef = React.createRef();
    this.setFocus = false;
    this.handleWorkingsChange = this.handleWorkingsChange.bind(this);
    this.handleCalculatorResult = this.handleCalculatorResult.bind(this);
  }

  componentDidUpdate() {
    if (this.setFocus) {
      const workingsElement = this.workingsRef.current;
      if (workingsElement) {
        if (this.cursorPosition) {
          setFocusPosition(workingsElement, this.cursorPosition);
          this.cursorPosition = null;
        } else {
          workingsElement.focus();
        }
      }
      this.setFocus = false;
    }
  }

  handleWorkingsChange(value, setFocus = false) {
    if (check.number(setFocus)) {
      this.setFocus = true;
      this.cursorPosition = setFocus;
    } else {
      this.setFocus = setFocus;
    }
    this.props.onChange(value);
  }

  handleCalculatorResult(_result) {
    const result = addCalculatorSymbols(_result);
    const { value } = this.props;
    const workingsElement = this.workingsRef.current;
    const { selectionStart, selectionEnd } = workingsElement;

    this.cursorPosition = selectionStart + result.length + 1;

    const workingsBeforeSelection = value.substring(0, selectionStart);
    const workingsAfterSelection = value.substring(selectionEnd);
    const newValue = `${workingsBeforeSelection}${result}\n${workingsAfterSelection}`;

    this.handleWorkingsChange(newValue, true);
  }

  render() {
    const { workingsCalcMode, hasWorkings, disableCalculator, classes } = this.props;

    if (workingsCalcMode !== 2 || (!hasWorkings && disableCalculator)) {
      return null;
    }

    const workingsProps = {
      value: this.props.value,
      workingsRef: this.workingsRef,
      onChange: this.handleWorkingsChange,
      autoExpand: true
    };

    let { evaluationType } = this.props;
    if (!check.assigned(evaluationType)) evaluationType = 1;

    const calculatorProps = {
      onResult: this.handleCalculatorResult,
      evaluationType
    };

    return (
      <Paper className={classes.paper}>
        <div className={classes.row}>
          {hasWorkings && (
            <div className={classes.workingsColumn}>
              <WorkingsTabs {...workingsProps}/>
            </div>
          )}
          {!disableCalculator && (
            <div className={classes.calculatorColumn}>
              <Calculator {...calculatorProps}/>
            </div>
          )}
        </div>
      </Paper>
    );
  }
}


// InlineWorkingsPanel (connected to styles)
// ------------------------------------------------------

const styles = (theme) => {
  const spacing = theme.spacing.unit * 2;

  return {
    paper: {
      margin: spacing,
      padding: spacing,
      backgroundColor: theme.palette.background.light
    },
    row: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start'
    },
    workingsColumn: {
      flexGrow: 1,
      minWidth: 0
    },
    calculatorColumn: {
      flexShrink: 0,
      marginLeft: spacing
    }
  };
};

InlineWorkingsPanel = withStyles(styles)(InlineWorkingsPanel);


// InlineWorkingsPanel (connected to store)
// ------------------------------------------------------

const mapStoreToProps = store => {
  const examData = getExamData(store);
  const contentData = getContentData(examData);
  const questionsData = getQuestionsData(contentData);
  const currentQuestionId = getCurrentQuestionId(contentData);
  const questionData = getQuestionDataById(questionsData)(currentQuestionId);

  const settingsData = getSettingsData(store);
  const clientSettingsData = getClientSettingsData(settingsData);

  return {
    value: getWorkings(getWorkingsData(questionData)),
    evaluationType: getCalculatorEvaluationMode(clientSettingsData),
    workingsCalcMode: getWorkingsCalcMode(clientSettingsData),
    hasWorkings: requiresWorkings(contentData, currentQuestionId),
    disableCalculator: !requiresCalculator(contentData, currentQuestionId),
    currentQuestionId
  };
};

const mapDispatchToProps = dispatch => ({
  onChange: questionId => value => {
    dispatch(saveWorkings(questionId, value));
  }
});

const mergeProps = (
  { value, currentQuestionId, evaluationType, workingsCalcMode, hasWorkings, disableCalculator },
  { onChange }
) => ({
  onChange: onChange(currentQuestionId),
  value,
  evaluationType,
  workingsCalcMode,
  hasWorkings,
  disableCalculator
});

const args = [mapStoreToProps, mapDispatchToProps, mergeProps];
InlineWorkingsPanel = connect(...args)(InlineWorkingsPanel);


// Export
// ------------------------------------------------------
export { InlineWorkingsPanel }
